// Shared part-outline rendering for packaging-plan SVGs.
//
// Used by:
//   - SalesOrderPage `SOPlannedBoxCard` (box-detail card on the SO page)
//   - LaserCurrentPackagingPage `BoxInteriorSVG` and `PalletMapSVG`
//
// The packaging plan stores a pre-rendered outline SVG per part on each
// zone (`zone.outline_svg_path`, an S3 URL). We fetch + parse those once
// per unique URL and inline the path data so the renderer can scale the
// part's actual bbox to fit any cell while keeping the stroke one device
// pixel wide via `vector-effect="non-scaling-stroke"`.

import { useEffect, useMemo, useState } from 'react';

// Walk a plan tree (or array of plans) and collect every unique
// `outline_svg_path` URL referenced by a zone.
const collectOutlineUrls = (planOrPlans) => {
  if (!planOrPlans) return [];
  const plans = Array.isArray(planOrPlans) ? planOrPlans : [planOrPlans];
  const set = new Set();
  for (const plan of plans) {
    if (!plan || !plan.pallets) continue;
    for (const p of plan.pallets) {
      for (const b of p.boxes || []) {
        for (const z of b.zones || []) {
          if (z.outline_svg_path) set.add(z.outline_svg_path);
        }
      }
    }
  }
  return Array.from(set);
};

// Hook: fetch + parse the outline SVG for every URL referenced by the
// given plan(s). Returns a map { url: { paths: string[], minX, minY,
// maxX, maxY } | null }. Missing entries (still loading) and failed
// fetches both surface as undefined / null so the renderer can skip
// gracefully.
//
// Pass either a single plan or an array (e.g. Object.values(plansBySo)).
export const useOutlinePaths = (planOrPlans) => {
  const [cache, setCache] = useState({});
  const urls = useMemo(() => collectOutlineUrls(planOrPlans), [planOrPlans]);
  useEffect(() => {
    const missing = urls.filter((u) => cache[u] === undefined);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const results = await Promise.all(missing.map(async (url) => {
        try {
          // cache: 'reload' forces a fresh CORS fetch — earlier code paths
          // that used <image href> caused browsers to cache an opaque
          // no-cors response that then poisoned subsequent CORS fetches
          // of the same URL.
          const res = await fetch(url, { cache: 'reload', mode: 'cors' });
          if (!res.ok) return [url, null];
          const text = await res.text();
          const paths = [];
          const re = /<path\s+d="([^"]+)"/g;
          let m;
          while ((m = re.exec(text)) !== null) paths.push(m[1]);
          // Tight bbox from M/L coords. The source SVG is authored in a
          // padded 100x100 viewBox; trusting the viewBox would render the
          // part as a small strip in a larger cell.
          let minX = Infinity, minY = Infinity;
          let maxX = -Infinity, maxY = -Infinity;
          const coordRe = /[ML]\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)/g;
          for (const d of paths) {
            let c;
            while ((c = coordRe.exec(d)) !== null) {
              const x = parseFloat(c[1]);
              const y = parseFloat(c[2]);
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
            coordRe.lastIndex = 0;
          }
          if (minX === Infinity) return [url, null];
          return [url, { paths, minX, minY, maxX, maxY }];
        } catch (_) {
          return [url, null];
        }
      }));
      if (cancelled) return;
      setCache((prev) => {
        const next = { ...prev };
        for (const [url, data] of results) next[url] = data;
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [urls]); // eslint-disable-line react-hooks/exhaustive-deps
  return cache;
};

// Inline outline renderer. Draws the parsed paths inside the target rect
// (cx, cy, cw, ch) by scaling the part's ACTUAL bbox (not the padded
// viewBox) to fit the cell. `vector-effect="non-scaling-stroke"` keeps
// the stroke one device pixel wide at any zoom so small features stay
// visible.
//
// When `rotated` is true, rotates the outline -90° around the part's
// bbox center so its natural long side aligns with the zone's long side
// (the packer places parts long-side along zone-x).
export const OutlineOverlay = ({
  data, cx, cy, cw, ch,
  rotated = false,
  autoOrient = false,
  strokeWidth = 0.9,
  opacity = 0.75,
  stroke = '#111',
}) => {
  if (!data || !data.paths || data.paths.length === 0) return null;
  const partW = Math.max(1e-6, data.maxX - data.minX);
  const partH = Math.max(1e-6, data.maxY - data.minY);
  // When autoOrient is set, decide rotation geometrically: rotate iff the
  // part's native aspect is crosswise to the cell's, so the long axis always
  // aligns with the cell's long axis (the physically-correct "as it sits"
  // orientation). This is more reliable than the packer's `rotated` flag —
  // that tracks pallet-space L/W bookkeeping independent of how the outline
  // SVG was authored, so on a part whose SVG already matches its footprint it
  // wrongly forces a 90° turn.
  const rot = autoOrient ? ((partW > partH) !== (cw > ch)) : rotated;
  const visW = rot ? partH : partW;
  const visH = rot ? partW : partH;
  const scale = Math.min(cw / visW, ch / visH);
  if (!isFinite(scale) || scale <= 0) return null;
  const drawW = visW * scale;
  const drawH = visH * scale;
  const tx = cx + (cw - drawW) / 2;
  const ty = cy + (ch - drawH) / 2;
  let transform;
  if (rot) {
    const pcx = (data.minX + data.maxX) / 2;
    const pcy = (data.minY + data.maxY) / 2;
    const newMinX = pcx + data.minY - pcy;
    const newMinY = pcy + pcx - data.maxX;
    transform = `translate(${tx}, ${ty}) scale(${scale}) translate(${-newMinX}, ${-newMinY}) rotate(-90 ${pcx} ${pcy})`;
  } else {
    transform = `translate(${tx}, ${ty}) scale(${scale}) translate(${-data.minX}, ${-data.minY})`;
  }
  return (
    <g transform={transform} opacity={opacity}>
      {data.paths.map((d, i) => (
        <path key={i} d={d} fill="none" stroke={stroke}
          strokeWidth={strokeWidth}
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round" strokeLinecap="round" />
      ))}
    </g>
  );
};

// Standalone "as-packed" part drawing. Renders the parsed outline filling a
// square viewport, rotated the SAME way the packing diagrams rotate it (via
// the `rotated` flag), so an operator-facing hero image agrees with the
// pallet/stack/box thumbnails instead of showing the part's raw CAD
// orientation. Drop it into any responsive square container (width/height
// 100%); `preserveAspectRatio` keeps the shape centered and undistorted.
export const PartOutlineImage = ({
  data,
  rotated = false,
  stroke = '#111',
  strokeWidth = 1.6,
  background = 'transparent',
}) => {
  if (!data || !data.paths || data.paths.length === 0) return null;
  const SZ = 100;   // square viewBox; OutlineOverlay scales the bbox to fit
  const PAD = 6;
  return (
    <svg
      viewBox={`0 0 ${SZ} ${SZ}`}
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
      style={{ background, display: 'block' }}
    >
      <OutlineOverlay
        data={data}
        cx={PAD} cy={PAD} cw={SZ - 2 * PAD} ch={SZ - 2 * PAD}
        rotated={rotated}
        stroke={stroke}
        strokeWidth={strokeWidth}
        opacity={1}
      />
    </svg>
  );
};
